import { useEffect, useLayoutEffect, useRef } from 'react';

import type { RefObject } from 'react';

const DEFAULT_INDEED_CONTAINER_SELECTOR =
  '#job .wcag-preview-hidden .external-apply-section #apply-with-indeed';

interface UseIndeedOverlayPortalOptions {
  overlaySlotRef: RefObject<HTMLDivElement | null>;
  hasIndeedProvider: boolean;
  isParsing: boolean;
  originalContainerSelector?: string;
}

export const useIndeedOverlayPortal = ({
  overlaySlotRef,
  hasIndeedProvider,
  isParsing,
  originalContainerSelector = DEFAULT_INDEED_CONTAINER_SELECTOR
}: UseIndeedOverlayPortalOptions): void => {
  const movedIndeedContainerRef = useRef<HTMLElement | null>(null);
  const originalIndeedParentRef = useRef<HTMLElement | null>(null);
  const originalIndeedNextSiblingRef = useRef<ChildNode | null>(null);

  useLayoutEffect(() => {
    if (!hasIndeedProvider) {
      return undefined;
    }

    const overlaySlot = overlaySlotRef.current;
    const originalIndeedContainer = document.querySelector<HTMLElement>(originalContainerSelector);

    if (!overlaySlot || !originalIndeedContainer) {
      return undefined;
    }

    movedIndeedContainerRef.current = originalIndeedContainer;
    originalIndeedParentRef.current = originalIndeedContainer.parentElement;
    originalIndeedNextSiblingRef.current = originalIndeedContainer.nextSibling;
    overlaySlot.appendChild(originalIndeedContainer);

    return () => {
      const movedIndeedContainer = movedIndeedContainerRef.current;
      const originalIndeedParent = originalIndeedParentRef.current;
      const originalIndeedNextSibling = originalIndeedNextSiblingRef.current;

      if (movedIndeedContainer) {
        movedIndeedContainer.removeAttribute('inert');
      }

      if (
        !movedIndeedContainer ||
        !originalIndeedParent?.isConnected ||
        movedIndeedContainer.parentElement !== overlaySlot
      ) {
        movedIndeedContainerRef.current = null;
        originalIndeedParentRef.current = null;
        originalIndeedNextSiblingRef.current = null;
        return;
      }

      if (originalIndeedNextSibling?.parentNode === originalIndeedParent) {
        originalIndeedParent.insertBefore(movedIndeedContainer, originalIndeedNextSibling);
      } else {
        originalIndeedParent.appendChild(movedIndeedContainer);
      }

      movedIndeedContainerRef.current = null;
      originalIndeedParentRef.current = null;
      originalIndeedNextSiblingRef.current = null;
    };
  }, [hasIndeedProvider, originalContainerSelector, overlaySlotRef]);

  useEffect(() => {
    const movedIndeedContainer = movedIndeedContainerRef.current;

    if (!hasIndeedProvider || !movedIndeedContainer) {
      return undefined;
    }

    movedIndeedContainer.toggleAttribute('inert', isParsing);

    return () => {
      movedIndeedContainer.removeAttribute('inert');
    };
  }, [hasIndeedProvider, isParsing]);
};
